import { useState, useEffect, useCallback, useRef } from "react";
import * as pharmaApi from "../services/pharmaApi";

/**
 * Hook to fetch product suggestions for PDV
 * - Same formula products (generic alternatives)
 * - Related products (complementary items)
 *
 * @param {Object} product - The selected product to get suggestions for
 * @param {number} storeId - The store ID for stock filtering
 * @returns {Object} - { sameFormula, relatedProducts, loading, error, clearSuggestions }
 */
const usePdvSuggestions = (product, storeId) => {
  const [sameFormula, setSameFormula] = useState({
    products: [],
    activePrinciple: null,
    enabled: false,
  });
  const [relatedProducts, setRelatedProducts] = useState({
    products: [],
    enabled: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Track previous product to avoid unnecessary fetches
  const previousProductIdRef = useRef(null);

  const fetchSuggestions = useCallback(async () => {
    if (!product?.id) {
      setSameFormula({ products: [], activePrinciple: null, enabled: false });
      setRelatedProducts({ products: [], enabled: false });
      return;
    }

    // Don't refetch for the same product
    if (previousProductIdRef.current === product.id) {
      return;
    }
    previousProductIdRef.current = product.id;

    setLoading(true);
    setError(null);

    try {
      // Fetch both in parallel using PharmaFlow API
      const [sameFormulaRes, relatedRes] = await Promise.allSettled([
        pharmaApi.getSameFormulaProducts(product.id, storeId, { onlyInStock: true, limit: 6 }),
        pharmaApi.getRelatedProducts(product.id, storeId, { onlyInStock: true, limit: 6 })
      ]);

      // Process same formula results
      if (sameFormulaRes.status === "fulfilled" && sameFormulaRes.value) {
        const data = sameFormulaRes.value;
        setSameFormula({
          products: data.sameFormulaProducts || data.products || [],
          activePrinciple: data.activePrinciple,
          enabled: data.featureEnabled !== false,
          searchedProduct: data.searchedProduct,
        });
      } else {
        setSameFormula({ products: [], activePrinciple: null, enabled: false });
      }

      // Process related products results
      if (relatedRes.status === "fulfilled" && relatedRes.value) {
        const data = relatedRes.value;
        setRelatedProducts({
          products: data.suggestions || data.products || [],
          enabled: data.featureEnabled !== false,
          searchedProduct: data.searchedProduct,
        });
      } else {
        setRelatedProducts({ products: [], enabled: false });
      }
    } catch (err) {
      console.error("Error fetching suggestions:", err);
      setError(err.message || "Erro ao buscar sugestões");
    } finally {
      setLoading(false);
    }
  }, [product?.id, storeId]);

  // Fetch when product changes
  useEffect(() => {
    fetchSuggestions();
  }, [fetchSuggestions]);

  // Clear suggestions manually
  const clearSuggestions = useCallback(() => {
    setSameFormula({ products: [], activePrinciple: null, enabled: false });
    setRelatedProducts({ products: [], enabled: false });
    previousProductIdRef.current = null;
  }, []);

  return {
    sameFormula,
    relatedProducts,
    loading,
    error,
    clearSuggestions,
    refetch: fetchSuggestions,
    hasSuggestions: sameFormula.products.length > 0 || relatedProducts.products.length > 0,
  };
};

export default usePdvSuggestions;
